// MetricsSandbox.js
import React, { useState, useEffect, useRef } from 'react';
import './Metricssandbox.css';
import lateralIcon from './rest.png';
import startImage from './Cyborg-bro.png';
import fullBatteryIcon from './energy.png';
import fatigueIcon from './fatigue.png';

export default function MetricsSandbox() {
  const [stage, setStage] = useState("start");
  const [mouseData, setMouseData] = useState([]);
  const [baseline, setBaseline] = useState({
    medSpeed: null,
    sigmaSpeed: null,
    medAccel: null,
    sigmaAccel: null,
    medDeviation: null,
    sigmaDeviation: null
  });
  const [averages, setAverages] = useState({
    avgSpeed: null,
    avgAccel: null,
    avgDeviation: null
  });
  const [score, setScore] = useState('0.00');
  const [breathing, setBreathing] = useState(false);
  const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false); // State for mobile popup
  const totalDots = 5;
  const radius = 8;
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState([]);
  const areaRef = useRef(null);

  // Detect if the user is on a mobile or tablet device
  const isMobileOrTablet = () => window.innerWidth < 1024; // Adjust threshold as needed
  const goToWhiteMatter = () => {
    window.location.href = '/products#whitematter';
  };

  // Helpers
  const average = arr => arr.reduce((s, x) => s + x, 0) / arr.length;
  const median = arr => {
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  const std = arr => {
    const m = average(arr);
    return Math.sqrt(average(arr.map(x => (x - m) ** 2)));
  };

  // Compute 2D speed & acceleration
  const computeSpeed = (a, b) => {
    const dt = (b.time - a.time) / 1000;
    if (!dt) return 0;
    return Math.hypot(b.x - a.x, b.y - a.y) / dt;
  };
  const computeAcceleration = (v0, v1, dt) =>
    dt === 0 ? 0 : (v1 - v0) / dt;

  // Compute deviation from straight line
  const computeDeviation = (currentPos, startPos, endPos) => {
    const lineVector = { x: endPos.x - startPos.x, y: endPos.y - startPos.y };
    const pointVector = { x: currentPos.x - startPos.x, y: currentPos.y - startPos.y };
    const lineLengthSquared = lineVector.x ** 2 + lineVector.y ** 2;
    if (lineLengthSquared === 0) return 0; // Avoid division by zero
    const projection = (pointVector.x * lineVector.x + pointVector.y * lineVector.y) / lineLengthSquared;
    const closestPoint = {
      x: startPos.x + projection * lineVector.x,
      y: startPos.y + projection * lineVector.y
    };
    return Math.hypot(currentPos.x - closestPoint.x, currentPos.y - closestPoint.y);
  };

  // Color based on score
  const colorFor = s => (s < 40 ? 'green' : s > 70 ? 'red' : '#FFBF00');

  // Feedback JSX
  const feedbackForScore = s => {
    const val = parseFloat(s);
    const col = colorFor(val);
    const linkStyle = { color: 'blue', textDecoration: 'none' };
    if (val < 40) {
      return (
        <>
          <p style={{ color: col }}>Stress level {val}%</p>
          <p>Current stress level low</p>
          <p>
            <a href="/download" style={linkStyle}>Download</a> WhiteMatter to manage your stress throughout the day!
          </p>
        </>
      );
    }
    if (val > 70) {
      return (
        <>
          <p style={{ color: col }}>Stress level {val}%</p>
          <p>High stress level detected. Box breathing activated</p>
          <p>
            <a href="/download" style={linkStyle}>Download</a> WhiteMatter to manage your stress throughout the day!
          </p>
        </>
      );
    }
    return (
      <>
        <p style={{ color: col }}>Stress level {val}%</p>
        <p>Current stress level: Medium-High</p>
        <p>
          <a href="/download" style={linkStyle}>Download</a> WhiteMatter to manage your stress throughout the day!
        </p>
      </>
    );
  };

  const iconForScore = s => {
    const val = parseFloat(s);
    if (val < 40) return fullBatteryIcon;
    if (val > 70) return fatigueIcon;
    return lateralIcon;
  };

  // Dot initialization
  useEffect(() => {
    if (stage === "lateral" && dots.length === 0 && areaRef.current) {
      const { width, height } = areaRef.current.getBoundingClientRect();
      const innerW = width - 2 * radius;
      const innerH = height - 2 * radius;
      setDots(
        Array.from({ length: totalDots }).map((_, i) => ({
          x: radius + ((i + 1) * innerW) / (totalDots + 1),
          y: radius + Math.random() * innerH
        }))
      );
    }
  }, [stage, dots]);

  // Track mouse movements
  useEffect(() => {
    if (stage !== "lateral") return;
    setMouseData([]);
    setBaseline({
      medSpeed: null,
      sigmaSpeed: null,
      medAccel: null,
      sigmaAccel: null,
      medDeviation: null,
      sigmaDeviation: null
    });
    const onMove = e =>
      setMouseData(d => [
        ...d,
        { x: e.clientX, y: e.clientY, time: Date.now() }
      ].slice(-50));
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [stage]);

  // Compute baseline when enough data
  useEffect(() => {
    if (stage === "lateral" &&
        baseline.medSpeed === null &&
        mouseData.length >= 12) {
      const speedRecs = mouseData.slice(1).map((b, i) => ({
        speed: computeSpeed(mouseData[i], mouseData[i + 1]),
        time: mouseData[i + 1].time
      }));
      const speeds = speedRecs.map(r => r.speed);
      const accels = speedRecs.slice(1).map((curr, i) => {
        const prev = speedRecs[i];
        const dt = (curr.time - prev.time) / 1000;
        return computeAcceleration(prev.speed, curr.speed, dt);
      });

      // Calculate deviations
      const deviations = [];
      for (let i = 0; i < progress; i++) {
        const startPos = dots[i];
        const endPos = dots[i + 1];
        const segmentData = mouseData.filter(d => d.time >= startPos.time && d.time <= endPos.time);
        segmentData.forEach(pos => {
          deviations.push(computeDeviation(pos, startPos, endPos));
        });
      }

      setBaseline({
        medSpeed: median(speeds),
        sigmaSpeed: std(speeds),
        medAccel: median(accels),
        sigmaAccel: std(accels),
        medDeviation: median(deviations),
        sigmaDeviation: std(deviations)
      });
    }
  }, [mouseData, baseline, stage, dots, progress]);

  const onDot = i => {
    if (i === progress) setProgress(p => p + 1);
  };

  // Finalize and compute score
  const finalize = () => {
    const { medSpeed, sigmaSpeed, medAccel, sigmaAccel, medDeviation, sigmaDeviation } = baseline;
    let pctSpeed = 0, pctAccel = 0, pctDeviation = 0;
    let avgSpeed = 0, avgAccel = 0, avgDeviation = 0;

    if (medSpeed && sigmaSpeed) {
      const speedRecs = mouseData.slice(1).map((b, i) => ({
        speed: computeSpeed(mouseData[i], mouseData[i + 1]),
        time: mouseData[i + 1].time
      }));
      const speeds = speedRecs.map(r => r.speed);
      avgSpeed = average(speeds);
      const zS = (avgSpeed - medSpeed) / sigmaSpeed;
      pctSpeed = zS <= 1 ? 0 : Math.min(((zS - 1) / 3) * 100, 100);
    }

    if (medAccel && sigmaAccel) {
      const speedRecs = mouseData.slice(1).map((b, i) => ({
        speed: computeSpeed(mouseData[i], mouseData[i + 1]),
        time: mouseData[i + 1].time
      }));
      const accels = speedRecs.slice(1).map((curr, i) => {
        const prev = speedRecs[i];
        const dt = (curr.time - prev.time) / 1000;
        return computeAcceleration(prev.speed, curr.speed, dt);
      });
      avgAccel = average(accels);
      const zA = (avgAccel - medAccel) / sigmaAccel;
      pctAccel = zA <= 1 ? 0 : Math.min(((zA - 1) / 3) * 100, 100);
    }

    if (medDeviation && sigmaDeviation) {
      const deviations = [];
      for (let i = 0; i < progress; i++) {
        const startPos = dots[i];
        const endPos = dots[i + 1];
        const segmentData = mouseData.filter(d => d.time >= startPos.time && d.time <= endPos.time);
        segmentData.forEach(pos => {
          deviations.push(computeDeviation(pos, startPos, endPos));
        });
      }
      avgDeviation = average(deviations);
      const zD = (avgDeviation - medDeviation) / sigmaDeviation;
      pctDeviation = zD <= 1 ? 0 : Math.min(((zD - 1) / 3) * 100, 100);
    }

    setAverages({ avgSpeed, avgAccel, avgDeviation });
    const finalScore = (pctSpeed + pctAccel + pctDeviation) / 3;
    setScore(finalScore.toFixed(2));
    setStage("loading");
    setTimeout(() => setStage("results"), 1200);
  };

  // Send to server on results
  useEffect(() => {
    if (stage !== 'results') return;
    const last = mouseData[mouseData.length - 1] || { x: null, y: null };
    const payload = {
      x: last.x,
      y: last.y,
      medSpeed: baseline.medSpeed,
      medAccel: baseline.medAccel,
      avgSpeed: averages.avgSpeed,
      avgAccel: averages.avgAccel,
      avgDeviation: averages.avgDeviation,
      score: parseFloat(score)
    };
    fetch('/api/lateral-stress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => { if (!res.ok) throw new Error(res.statusText); })
      .catch(console.error);
  }, [stage]);

  // Auto-breathing
  useEffect(() => {
    if (stage === "results" && parseFloat(score) > 80) {
      setBreathing(true);
    }
  }, [stage, score]);

  const reset = () => {
    setStage("start");
    setProgress(0);
    setDots([]);
    setMouseData([]);
    setBaseline({
      medSpeed: null,
      sigmaSpeed: null,
      medAccel: null,
      sigmaAccel: null,
      medDeviation: null,
      sigmaDeviation: null
    });
    setAverages({ avgSpeed: null, avgAccel: null, avgDeviation: null });
    setScore('0.00');
    setBreathing(false);
  };

  const renderContent = () => {
    switch (stage) {
      case "start":
        return (
          <div className="msb-container glass-card fade-in">
            <p className="msb-tagline">
              Evaluate your psychological stress response with a quick cognitive test.
            </p>
            <div className="msb-cta">
              <button
                className="blue-button1 neon-button"
                onClick={() => {
                  if (isMobileOrTablet()) {
                    setIsMobilePopupOpen(true); // Open popup if on mobile/tablet
                  } else {
                    setStage("lateral"); // Proceed to test if on desktop
                  }
                }}
              >
                Start Test
              </button>
              <button className="blue-button" onClick={goToWhiteMatter}>Learn More</button>
            </div>
            <img src={startImage} alt="Stress assessment preview" className="msb-image" />
            {/* Mobile/Tablet Popup */}
            {isMobilePopupOpen && (
              <div className="popup-overlay">
                <div className="popup-content">
                  <p>Please use a desktop to take the test.</p>
                  <button
                    className="blue-button1 neon-button"
                    onClick={() => setIsMobilePopupOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case "lateral":
        return (
          <div className="msb-container glass-card fade-in">
            <h2>
              Begin by clicking on the blue dot, which will turn green.<br/>
              Continue connecting the dots in sequence, then click “Finish Test.”
            </h2>
            <div ref={areaRef} className="dot-area">
              <svg width="100%" height="100%">
                {progress > 0 && (
                  <polyline
                    points={dots.slice(0, progress).map(d => `${d.x},${d.y}`).join(' ')}
                    stroke="#3BB54A" strokeWidth="4" fill="none"
                  />
                )}
                {dots.map((d, i) => (
                  <circle
                    key={i}
                    cx={d.x} cy={d.y} r={radius}
                    fill={i < progress ? '#3BB54A' : i === progress ? '#1976D2' : '#CFD8DC'}
                    stroke="#455A64" strokeWidth="2"
                    onClick={() => onDot(i)}
                    style={{ cursor: i === progress ? 'pointer' : 'default' }}
                  />
                ))}
              </svg>
            </div>
            <button
              className="blue-button1 neon-button"
              disabled={progress < totalDots}
              onClick={finalize}
            >
              Finish Test
            </button>
          </div>
        );
      case "loading":
        return (
          <div className="msb-container glass-card fade-in">
            <h2>Analyzing…</h2>
          </div>
        );
      case "results":
        return (
          <div className="msb-container glass-card fade-in">
            <h2>Test Complete</h2>
            <div className="result-row">
              <img
                src={iconForScore(score)}
                className="icon2"
                style={{ filter: `drop-shadow(0 0 6px ${colorFor(parseFloat(score))})` }}
              />
              <div className="text-block">
                <div className="value" style={{ color: colorFor(parseFloat(score)) }}>
                  {score}%
                </div>
              </div>
            </div>
            <div className="feedback">
              {feedbackForScore(score)}
            </div>
            {breathing ? (
              <div className="breathing-box">
                <svg viewBox="0 0 100 100" className="breath-svg">
                  <rect className="breath-rect" x="2" y="2" width="96" height="96" />
                </svg>
                <div className="breath-text" />
              </div>
            ) : (
              <button className="blue-button1 neon-button" onClick={() => setBreathing(true)}>
                Activate Box Breathing
              </button>
            )}
            <button className="blue-button neon-button reset" onClick={reset}>
              Reset Test
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return <section className="msb-section">{renderContent()}</section>;
}